if (!customElements.get('collection-card-options')) {
  customElements.define(
    'collection-card-options',
    class CollectionCardOptions extends HTMLElement {
      connectedCallback() {
        this.card = this.closest('.card');
        this.wrapper = this.closest('.card-wrapper');
        this.variantInput = this.card?.querySelector('[data-card-variant-id]');
        this.addButton = this.card?.querySelector('.collection-card-options__add');
        this.productUrl = this.dataset.productUrl;
        this.highVariantProduct = this.dataset.highVariantProduct === 'true';

        try {
          this.variants = JSON.parse(this.querySelector('[data-card-variants]')?.textContent || '[]');
        } catch (error) {
          this.variants = [];
        }

        this.querySelectorAll('[data-card-option-value]').forEach((button) => {
          button.addEventListener('click', () => this.selectValue(button));
        });

        this.querySelectorAll('[data-card-option-index]').forEach((group) => {
          const rail = group.querySelector('[data-card-option-values]');
          this.enableDragScroll(rail);
          this.enableArrowScroll(group, rail);
        });

        this.updateVariant({ preserveLiquidAvailability: this.highVariantProduct });
      }

      async selectValue(button) {
        const group = button.closest('[data-card-option-index]');
        group.querySelectorAll('[data-card-option-value]').forEach((item) => {
          const selected = item === button;
          item.classList.toggle('is-selected', selected);
          item.setAttribute('aria-pressed', String(selected));
        });
        if (this.highVariantProduct) await this.loadHighVariantOptions(button);
        this.updateVariant();
      }

      selectedOptionValueIds() {
        return [...this.querySelectorAll('[data-card-option-index]')]
          .map((group) => group.querySelector('.is-selected')?.dataset.cardOptionValueId)
          .filter(Boolean);
      }

      async loadHighVariantOptions(button) {
        if (!this.productUrl || !button?.dataset.cardOptionValueId) return;
        const optionValues = this.selectedOptionValueIds();
        const url = new URL(this.productUrl, window.location.origin);
        url.searchParams.set('option_values', optionValues.join(','));
        this.classList.add('is-loading');

        try {
          const response = await fetch(url.toString(), { headers: { Accept: 'text/html' } });
          if (!response.ok) throw new Error('Unable to load product option availability.');
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const data = html.querySelector('[data-tees-polos-current-colour-variants]');
          const variants = data ? JSON.parse(data.textContent || '[]') : [];
          variants.forEach((variant) => {
            const index = this.variants.findIndex((item) => String(item.id) === String(variant.id));
            if (index === -1) this.variants.push(variant);
            else this.variants[index] = variant;
          });
        } catch (error) {
          console.error(error);
        } finally {
          this.classList.remove('is-loading');
        }
      }

      selectedOptions() {
        const selected = [...(this.variants[0]?.options || [])];
        this.querySelectorAll('[data-card-option-index]').forEach((group) => {
          const index = Number(group.dataset.cardOptionIndex);
          selected[index] = group.querySelector('.is-selected')?.dataset.cardOptionValue || selected[index];
        });
        return selected;
      }

      enableDragScroll(rail) {
        if (!rail) return;
        let dragging = false;
        let moved = false;
        let suppressClick = false;
        let startX = 0;
        let startScroll = 0;

        rail.addEventListener('pointerdown', (event) => {
          dragging = true;
          moved = false;
          suppressClick = false;
          startX = event.clientX;
          startScroll = rail.scrollLeft;
          rail.classList.add('is-dragging');
        });
        rail.addEventListener('pointermove', (event) => {
          if (!dragging) return;
          const distance = event.clientX - startX;
          if (Math.abs(distance) > 8) moved = true;
          rail.scrollLeft = startScroll - distance;
          if (moved) event.preventDefault();
        });
        const stopDragging = () => {
          if (!dragging) return;
          dragging = false;
          suppressClick = moved;
          rail.classList.remove('is-dragging');
          window.setTimeout(() => {
            moved = false;
            suppressClick = false;
          }, 0);
        };
        rail.addEventListener('pointerup', stopDragging);
        rail.addEventListener('pointercancel', stopDragging);
        window.addEventListener('pointerup', stopDragging);
        window.addEventListener('pointercancel', stopDragging);
        rail.addEventListener('click', (event) => {
          if (suppressClick) {
            event.preventDefault();
            event.stopImmediatePropagation();
          }
        }, true);
      }

      enableArrowScroll(group, rail) {
        if (!group || !rail) return;
        const previous = group.querySelector('[data-card-option-previous]');
        const next = group.querySelector('[data-card-option-next]');
        if (!previous || !next) return;

        const updateControls = () => {
          const hasOverflow = rail.scrollWidth > rail.clientWidth + 2;
          previous.hidden = !hasOverflow;
          next.hidden = !hasOverflow;
          previous.disabled = !hasOverflow || rail.scrollLeft <= 1;
          next.disabled = !hasOverflow || rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 1;
        };
        const scrollOptions = (direction) => {
          rail.scrollBy({
            left: direction * Math.max(80, rail.clientWidth * 0.7),
            behavior: 'smooth'
          });
        };

        previous.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          scrollOptions(-1);
        });
        next.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          scrollOptions(1);
        });
        rail.addEventListener('scroll', updateControls, { passive: true });

        if ('ResizeObserver' in window) {
          const observer = new ResizeObserver(updateControls);
          observer.observe(rail);
        }
        requestAnimationFrame(updateControls);
      }

      updateVariant({ preserveLiquidAvailability = false } = {}) {
        const selected = this.selectedOptions();
        const exactVariant = this.variants.find((variant) =>
          variant.options.every((value, index) => value === selected[index])
        );
        const availableVariant = exactVariant?.available
          ? exactVariant
          : this.variants.find((variant) =>
              variant.available && variant.options.every((value, index) => !selected[index] || value === selected[index])
            );
        const variant = exactVariant || availableVariant;

        if (this.variantInput) {
          this.variantInput.value = variant?.id || '';
          this.variantInput.disabled = !variant?.available;
          this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
        if (this.addButton) {
          this.addButton.disabled = !variant?.available;
          this.addButton.setAttribute(
            'aria-label',
            variant?.available ? 'Add selected options to cart' : 'Selected options are sold out'
          );
        }

        if (!preserveLiquidAvailability) this.updateAvailability(selected);
        this.updateImage(variant);
      }

      updateAvailability(selected) {
        this.querySelectorAll('[data-card-option-index]').forEach((group) => {
          const optionIndex = Number(group.dataset.cardOptionIndex);
          group.querySelectorAll('[data-card-option-value]').forEach((button) => {
            const candidate = [...selected];
            candidate[optionIndex] = button.dataset.cardOptionValue;
            const possible = this.variants.some(
              (variant) => variant.available && variant.options.every((value, index) => !candidate[index] || value === candidate[index])
            );
            button.classList.toggle('is-unavailable', !possible);
            button.setAttribute('aria-disabled', String(!possible));
          });
        });
      }

      updateImage(variant) {
        const source = variant?.featured_image?.src;
        const image = this.card?.querySelector('.card__media img');
        if (!source || !image) return;
        image.src = `${source}${source.includes('?') ? '&' : '?'}width=533`;
        image.removeAttribute('srcset');
      }
    }
  );
}
